import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Clock, Save, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { clientApi } from '../utils/api';
import { sessionStorage } from '../utils/auth-api';
import { API_BASE } from '../utils/api-base';

interface Client {
  id: string;
  name: string;
  pollingIntervalMs?: number;
}

interface PollingSettings {
  value: number;
  unit: 'seconds' | 'minutes';
}

interface PollingIntervalConfigProps {
  token?: string;
}

export function PollingIntervalConfig({ token: propToken }: PollingIntervalConfigProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [pollingSettings, setPollingSettings] = useState<PollingSettings>({
    value: 1,
    unit: 'seconds',
  });
  const [currentIntervalMs, setCurrentIntervalMs] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get token from prop or session storage
  const getToken = () => {
    if (propToken) return propToken;
    const session = sessionStorage.get();
    return session?.token || session?.session?.token || '';
  };

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  // Load polling interval when client is selected
  useEffect(() => {
    if (selectedClient) {
      const client = clients.find((c) => c.id === selectedClient);
      if (client) {
        setCurrentIntervalMs(client.pollingIntervalMs || 5000);
        const ms = client.pollingIntervalMs || 5000;
        
        if (ms >= 60000) {
          setPollingSettings({
            value: Math.round(ms / 60000),
            unit: 'minutes',
          });
        } else {
          setPollingSettings({
            value: Math.round(ms / 1000),
            unit: 'seconds',
          });
        }
      }
    }
  }, [selectedClient, clients]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE}/api/clients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch clients (HTTP ${response.status})`);
      }

      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('❌ Get clients error:', error);
      toast.error('Failed to load clients. Try logging in again.');
    } finally {
      setLoading(false);
    }
  };

  const convertToMs = (value: number, unit: 'seconds' | 'minutes'): number => {
    switch (unit) {
      case 'seconds':
        return value * 1000;
      case 'minutes':
        return value * 60 * 1000;
      default:
        return value * 1000;
    }
  };

  const convertFromMs = (ms: number): PollingSettings => {
    if (ms >= 60000) {
      return { value: Math.round(ms / 60000), unit: 'minutes' };
    } else {
      return { value: Math.round(ms / 1000), unit: 'seconds' };
    }
  };

  const formatInterval = (ms: number): string => {
    const settings = convertFromMs(ms);
    return `${settings.value} ${settings.unit}`;
  };

  const handleSave = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    if (pollingSettings.value <= 0) {
      toast.error('Polling interval must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      const intervalMs = convertToMs(pollingSettings.value, pollingSettings.unit);

      // Validate against limits (1 second - 60 minutes)
      if (intervalMs < 1000) {
        toast.error('Polling interval cannot be less than 1 second');
        return;
      }
      if (intervalMs > 3600000) {
        toast.error('Polling interval cannot exceed 60 minutes');
        return;
      }

      const token = getToken();
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE}/api/clients/${selectedClient}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          pollingIntervalMs: intervalMs,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update client (HTTP ${response.status})`);
      }

      setCurrentIntervalMs(intervalMs);
      toast.success(`Polling interval updated to ${formatInterval(intervalMs)}`);
      
      // Refresh clients list
      await loadClients();
    } catch (error) {
      console.error('Failed to update polling interval:', error);
      toast.error('Failed to update polling interval. Ensure backend API is running.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-500" />
          <div>
            <CardTitle>Data Polling Interval</CardTitle>
            <CardDescription>Configure how often data is fetched for each client</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Interval Range:</strong> 1 second to 60 minutes. Shorter intervals mean faster updates and higher network load.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {/* Client Selection */}
          <div className="space-y-2">
            <Label htmlFor="client-select">Select Client</Label>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger id="client-select">
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Current Interval Display */}
          {selectedClient && currentIntervalMs !== null && (
            <div className="rounded-lg bg-gradient-to-r from-slate-900/50 to-slate-800/30 p-4 border border-slate-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Polling Interval</p>
                  <p className="text-lg font-bold text-slate-100">{formatInterval(currentIntervalMs)}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {currentIntervalMs}ms
                </Badge>
              </div>
            </div>
          )}

          {/* Polling Interval Input */}
          {selectedClient && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="interval-value">Interval Value</Label>
                  <Input
                    id="interval-value"
                    type="number"
                    min="1"
                    max="3600"
                    step="1"
                    value={pollingSettings.value}
                    onChange={(e) =>
                      setPollingSettings({
                        ...pollingSettings,
                        value: Math.max(1, parseInt(e.target.value) || 1),
                      })
                    }
                    placeholder="Enter value"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interval-unit">Unit</Label>
                  <Select value={pollingSettings.unit} onValueChange={(value: any) =>
                    setPollingSettings({ ...pollingSettings, unit: value })
                  }>
                    <SelectTrigger id="interval-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="seconds">Seconds</SelectItem>
                      <SelectItem value="minutes">Minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* New Value Preview */}
              <div className="rounded-lg bg-gradient-to-r from-blue-900/30 to-cyan-900/30 p-4 border border-blue-700/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">New Polling Interval</p>
                    <p className="text-lg font-bold text-blue-300">
                      {pollingSettings.value} {pollingSettings.unit}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {convertToMs(pollingSettings.value, pollingSettings.unit)}ms
                  </Badge>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={saving || !selectedClient}
                className="w-full gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : `Save Interval (${convertToMs(pollingSettings.value, pollingSettings.unit)}ms)`}
              </Button>

              {/* Info */}
              <div className="rounded-lg bg-slate-900/30 p-3 border border-slate-700/50">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  💡 <strong>Tip:</strong> Use longer intervals like 1 to 5 minutes for stable systems. Use shorter intervals like 1 to 5 seconds only where near-real-time monitoring is actually needed.
                </p>
              </div>
            </>
          )}

          {!selectedClient && (
            <div className="rounded-lg bg-slate-900/30 p-4 border border-slate-700/50 text-center">
              <p className="text-sm text-muted-foreground">Select a client to configure polling interval</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
